import { PastMeetingsType } from './meetings.service'
import { MeetingFilters } from './filters/filters.component'

const me = 'me'
const all = 'all'

export const queryKeys = {
  meetings: {
    base: ['meetings'],

    support: {
      all: ['support', 'meetings', 'all'],
      completed: ['support', 'meetings', 'completed'],
      today: ['support', 'meetings', 'today'],
      byId: (id: number) => ['support', 'meetings', id],
      byUserId: (userId: number) => ['support', 'meetings', 'user', userId],
      byClientId: (clientId: number) => ['support', 'meetings', 'client', clientId],
      debugData: (id: number) => ['support', 'meetings', id, 'debug-data'],
      hallucinationAudit: (meetingId: number, jobId: number) => ['support', 'meetings', meetingId, 'hallucination-audit', jobId],
      editorDebug: (id: number) => ['support', 'meetings', id, 'editor-debug'],
      agendaTranscriptMappings: ['support', 'meetings', 'agenda-transcript-mappings'],
      agendaTranscriptMapping: (meetingId: number, jobId: number) => ['support', 'meetings', meetingId, 'agenda-transcript-mapping', jobId],
    },

    client: {
      base: ['client-meetings'],
      list: (params: object) => ['client-meetings', 'list', params],
      retentionExtensions: (meetingId: number) => ['client-meetings', meetingId, 'retention-extensions'],
    },

    retentionExtensionRequests: {
      base: ['meetings', 'retention-extension-requests'],
      options: (meetingId: number) => ['meetings', 'retention-extension-requests', meetingId, 'options'],
      byId: (meetingId: number, requestId: number) => ['meetings', 'retention-extension-requests', meetingId, requestId],
    },

    byId: (id: number) => ['meetings', id],
    list: {
      base: ['meeting-list'],
      upcoming: ['meeting-list', 'upcoming'],
      ongoing: ['meeting-list', 'ongoing'],
      past: {
        base: ['meeting-list', 'past'],
        byType: (type: PastMeetingsType) => ['meeting-list', 'past', type],
      },
    },
    // Under the meeting's own key, so that invalidating the meeting refreshes its entities along with it.
    entities: {
      byMeetingId: (meetingId: number) => ['meetings', meetingId, 'entities'],
      citations: (meetingId: number, meetingEntityId: number) => ['meetings', meetingId, 'entities', meetingEntityId, 'citations'],
    },
    actionItems: {
      byMeetingId: (meetingId: number) => ['meetings', meetingId, 'action-items'],
    },
  },

  users: {
    base: ['users'],
    byId: (id: number) => ['users', id],
    byClientId: (clientId: number) => ['users', 'client', clientId],
    me: ['users', me],
    calendarSyncStatus: ['users', me, 'calendar-sync-status'],
    e2eTestUserOfflineToken: ['users', 'e2e-test-user', 'offline-token'],
    settings: ['user-settings'],
    search: (query: string) => ['users', 'search', query],
  },

  editorPollingFallback: {
    base: ['admin', 'editor-polling-fallback'],
  },

  demoProfiles: {
    base: ['demo-profiles'],
    byId: (id: number) => ['demo-profiles', id],
  },

  intercom: {
    base: ['intercom'],
    admins: ['intercom', 'admins'],
  },

  supportConversations: {
    base: ['support-conversations'],
    list: (filters: unknown) => ['support-conversations', 'list', filters],
    waitingCount: ['support-conversations', 'waiting-count'],
    viewer: ['support-conversations', 'viewer'],
    knowledgeGaps: ['support-conversations', 'knowledge-gaps'],
    detail: (intercomId: string) => ['support-conversations', 'detail', intercomId],
    context: (intercomId: string) => ['support-conversations', 'context', intercomId],
    replyImages: ['support-conversations', 'reply-images'],
  },

  search: {
    results: {
      byFilters: (filters: MeetingFilters) => ['search-results', filters],
    },
    attendees: ['search-attendees'],
    speakers: ['search-speakers'],
    guestOrganizations: ['search-guest-organizations'],
  },

  clients: {
    base: ['clients'],
    all: ['clients', all],
    me: ['clients', me],
    users: ['clients', me, 'users'],
    byId: (id: number) => ['clients', id],
  },

  teams: {
    base: ['teams'],
    byId: (id: number) => ['teams', id],
    // Nested under byId on purpose: the support view returns a different shape, but invalidating
    // byId should still refresh it.
    supportById: (id: number) => ['teams', id, 'support'],
    me: ['teams', me],
    all: ['teams', all],
    users: ['team-users'],
    settings: ['team-settings'],
  },

  guestOrganizations: {
    base: ['guest-organizations'],
    all: ['all-guest-organizations'],
  },
  meetingProfiles: {
    base: ['meeting-profiles'],
    all: ['meeting-profiles', all],
    own: ['meeting-profiles', 'own'],
    byId: (id: number) => ['meeting-profiles', id],
    byClientId: (clientId: number) => ['meeting-profiles', 'client', clientId],
  },

  templates: {
    base: ['templates'],
    all: ['templates', all],
    byMeetingId: (meetingId: number) => ['templates', 'by-meeting-id', meetingId],
    byId: (id: number) => ['templates', id],
    // The own-client and support lists return different scopes, so they need separate cache
    // entries. They stay under `base` so invalidating it still refreshes every model list.
    models: {
      base: ['templates', 'models'],
      own: ['templates', 'models', 'own'],
      support: ['templates', 'models', 'support'],
      byClient: (clientId: number) => ['templates', 'models', clientId],
    },
  },

  experiments: {
    base: ['experiments'],
    all: ['experiments', 'all'],
    byId: (id: number) => ['experiments', id],
  },

  llmDefaults: {
    base: ['llm-defaults'],
    all: ['llm-defaults', all],
    jobTasks: ['llm-defaults', 'job-tasks'],
  },

  evaluators: {
    base: ['evaluators'],
    all: ['evaluators', all],
    byId: (id: number) => ['evaluators', id],
  },

  evalDatasets: {
    base: ['eval-datasets'],
    all: ['eval-datasets', all],
    runs: (id: number) => ['eval-datasets', id, 'runs'],
    runResults: (id: number, runId: number) => ['eval-datasets', id, 'runs', runId, 'results'],
  },

  llmTracing: {
    base: ['llm-tracing'],
    skeletonByJob: (meetingBatchJobRunId: number) => ['llm-tracing', 'skeleton', meetingBatchJobRunId],
    systemPromptsByJob: (meetingBatchJobRunId: number) => ['llm-tracing', 'system-prompts', meetingBatchJobRunId],
    spanById: (spanId: string) => ['llm-tracing', 'span', spanId],
  },

  metrics: {
    // These take the request parameters so that each filter combination gets its own cache entry.
    // Sharing one entry across combinations makes a changed filter resolve to the previous
    // combination's data, which is then rendered as if it were current until the refetch lands.
    list: (params: object) => ['metrics', 'list', params],
    completedMeetings: (params: object) => ['metrics', 'completed-meetings', params],
    featureAdoptionByUser: (teamIds: number[]) => ['metrics', 'feature-adoption-by-user', ...teamIds],
    engagementByUser: (teamIds: number[]) => ['metrics', 'engagement-by-user', ...teamIds],
    usageByUser: (type: string, params: object) => ['metrics', 'usage-by-user', type, params],
    usageByClient: (params: object) => ['metrics', 'usage-by-client', params],
    meetingRoomMetrics: (clientIds: number[]) => ['metrics', 'meeting-room-metrics', ...clientIds],
    sla: (clientIds: number[], month: string) => ['metrics', 'sla', month, ...clientIds],
    performance: (teamIds: number[], startDate: string | null, endDate: string | null) => ['metrics', 'performance', teamIds, startDate, endDate],
    activity: (teamIds: number[], startDate: string, endDate: string, precision: string) => ['metrics', 'activity', teamIds, startDate, endDate, precision],
    // The list itself is keyed by the table's own source; this is the prefix it caches under, so that
    // the rows and the facet option lists share one entry.
    activityLog: () => ['metrics', 'activity-log'],
    activityLogCoverage: () => ['metrics', 'activity-log', 'coverage'],
    // Takes no parameters: the endpoint answers for the caller's whole client and its own period.
    subscriptionUsage: () => ['metrics', 'subscription-usage'],
  },
}
